/**
 * The mobile app's design tokens, on the web.
 *
 * A supervisor on an iPhone gets the browser instead of the Play Store build,
 * and the two are demonstrated side by side in the same room — so the pills,
 * cards and buttons here resolve to the same hexes as
 * `mobile-frontend/src/constants/theme.ts`. Keep the two files in step: a value
 * changed on one side and not the other is a screen that stops matching the
 * phone being held next to it.
 */

const neutral = {
  0: '#FFFFFF',
  25: '#FBFCFD',
  50: '#F4F6F8',
  100: '#E9EDF1',
  200: '#D8DEE5',
  300: '#B9C2CC',
  400: '#8D97A3',
  500: '#68727E',
  600: '#4D5661',
  700: '#39424C',
  800: '#252C34',
  900: '#151A20',
};

const accent = {
  DEFAULT: '#1B5E8C',
  light: '#2C7099',
  dark: '#134667',
  wash: '#EDF3F7',
};

const semantic = {
  critical: '#A3312A',
  criticalWash: '#FBF0EF',
  caution: '#845009',
  cautionWash: '#FAF4EA',
  positive: '#2F6B4C',
  positiveWash: '#EFF5F1',
};

export const Colors = {
  neutral,
  semantic,
  accent,
  background: { primary: neutral[0], secondary: neutral[50], tertiary: neutral[100] },
  text: {
    primary: neutral[900],
    secondary: neutral[500],
    tertiary: neutral[400],
    disabled: neutral[300],
    inverse: neutral[0],
  },
  border: { default: neutral[200], subtle: neutral[100] },
  status: {
    success: semantic.positive,
    warning: semantic.caution,
    error: semantic.critical,
    info: accent.DEFAULT,
    neutral: neutral[500],
  },
};

/**
 * Badge colours as a set, so a pill never hand-mixes `colour + '15'`.
 * `tone('critical')` gives a readable foreground on its own wash.
 */
export const tone = (name) => ({
  neutral: { fg: neutral[600], bg: neutral[50], border: neutral[200] },
  accent: { fg: accent.DEFAULT, bg: accent.wash, border: '#CFDEE8' },
  critical: { fg: semantic.critical, bg: semantic.criticalWash, border: '#EBD4D2' },
  caution: { fg: semantic.caution, bg: semantic.cautionWash, border: '#E8DCC4' },
  positive: { fg: semantic.positive, bg: semantic.positiveWash, border: '#D2E2D8' },
}[name] || { fg: neutral[600], bg: neutral[50], border: neutral[200] });

/** Pieces, the way the floor writes them. */
export const pcs = (n) => `${Number(n || 0).toLocaleString('en-IN')} pcs`;

/** The greeting the board opens with, by the clock. */
export function greetingKey() {
  const h = new Date().getHours();
  if (h < 12) return 'good_morning';
  if (h < 17) return 'good_afternoon';
  return 'good_evening';
}

/** The pill tone an order or job status reads in. */
export const statusTone = (status) => ({
  delivered: 'positive', completed: 'positive', complete: 'positive',
  ready_for_dispatch: 'positive', ready: 'positive',
  in_production: 'accent', in_progress: 'accent', dispatched: 'accent', design: 'accent',
  pending: 'caution', ready_for_stock: 'caution',
  on_hold: 'critical', cancelled: 'critical',
}[status] || 'neutral');

export const priorityTone = (priority) => ({
  high: 'critical', urgent: 'critical', medium: 'caution', low: 'positive',
}[priority] || 'neutral');
